import { useRouter } from '@application/utils';
import type { DogBreedInterestSubscriptionQuery, QuizResultsGetBreedsQuery } from '@gql/graphql';
import { AnimalSizeEnum } from '@gql/graphql';
import type { CardItemBreedQuizResultData } from '@presentation/components/CardItemBreedQuizResult';
import cleanDeep from 'clean-deep';
import { useMemo } from 'react';

const shouldAddItem = (
  answerKey: number,
  answers: any,
  breed: NonNullable<QuizResultsGetBreedsQuery['findManyDogBreed']>[number],
): boolean => {
  const answer = answers[answerKey];

  // Question: Time alone
  if (answerKey === 7) {
    if (answer === '4-8') {
      if ([2, 3, 4, 5].indexOf(breed.independenceIndex ?? -1) < 0) return false;
    }
    if (answer === 'more-8') {
      if ([1, 2, 3, 4].indexOf(breed.physicalActivityNeedIndex ?? -1) < 0) return false;
      if ([1, 2].indexOf(breed.educationIndex ?? -1) < 0) return false;
      if ([2, 3, 4, 5].indexOf(breed.independenceIndex ?? -1) < 0) return false;
    }
  }

  // Question: Education
  if (answerKey === 8) {
    if (answer === 'no') {
      if ([1, 2].indexOf(breed.educationIndex ?? -1) < 0) return false;
      if ([1, 2, 3, 4].indexOf(breed.predatoryInstinctIndex ?? -1) < 0) return false;
      if ([2, 3].indexOf(breed.sociabilityIndex ?? -1) < 0) return false;
    }
  }

  // Question: Guests
  if (answerKey === 9) {
    if (answer === 'yes') {
      if ([2, 3, 4, 5].indexOf(breed.foreignerSociabilityIndex ?? -1) < 0) return false;
      if ([2, 3].indexOf(breed.sociabilityIndex ?? -1) < 0) return false;
      if ([1, 2, 3].indexOf(breed.barkingTendancyIndex ?? -1) < 0) return false;
    }
  }

  // Question: Animal size
  if (answerKey === 10) {
    if (!breed.sizeCategory) return false;
    if (answer === 'small') {
      if ([AnimalSizeEnum.SMALL].indexOf(breed.sizeCategory) < 0) return false;
    }
    if (answer === 'medium') {
      if ([AnimalSizeEnum.MEDIUM].indexOf(breed.sizeCategory) < 0) return false;
    }
    if (answer === 'big') {
      if ([AnimalSizeEnum.BIG].indexOf(breed.sizeCategory) < 0) return false;
    }
  }

  // Question: Maintenanance
  if (answerKey === 11) {
    if (answer === 'low-maintenance') {
      if ([1].indexOf(breed.careIndex ?? -1) < 0) return false;
    }
    if (answer === 'medium-maintenance') {
      if ([1, 2].indexOf(breed.careIndex ?? -1) < 0) return false;
    }
    if (answer === 'big-maintenance') {
      if ([1, 2, 3].indexOf(breed.careIndex ?? -1) < 0) return false;
    }
  }

  return true;
};

const getMatchPercentage = (
  answers: any,
  breed: NonNullable<QuizResultsGetBreedsQuery['findManyDogBreed']>[number],
): number => {
  // 80% of the score depends on the quizz answers
  let nonMatches = 0;
  // eslint-disable-next-line no-restricted-syntax
  for (const answerKey of [7, 8, 9, 10, 11]) {
    if (answers[answerKey as keyof typeof answers] != null) {
      if (!shouldAddItem(answerKey, answers, breed)) {
        nonMatches += 1;
      }
    }
  }
  // Since TC-1585, 20% of the score depends on the litter / breeder availability

  const hasPublicLitters =
    breed.litters.filter((x) => x.adminValidated === true && x.archived === false).length > 0;
  const hasPublicBreeders =
    (breed.dogBreeders ?? [])
      .map((item) => item.dogBreeder)
      .filter((x) => !!x)
      .filter((x) => x.public === true).length > 0;

  if (!hasPublicBreeders && !hasPublicLitters) {
    nonMatches += 3; // subtract 20% from the score
  } else if (!hasPublicLitters) {
    nonMatches += 2; // subtract 13.3 % from the score
  } else if (!hasPublicBreeders) {
    nonMatches += 1; // subtract 6.7 % from the score
  }

  const matchPercentage = (15 - nonMatches) * (1 / 15);
  return matchPercentage;
};

export const useFormatBreeds = (
  answers: any,
  breeds?: QuizResultsGetBreedsQuery['findManyDogBreed'] | null,
  breedogBreedInterestSubscription?:
    | NonNullable<DogBreedInterestSubscriptionQuery['findManyDogBreedInterestSubscription']>[number]
    | null,
) => {
  const { routeLocationDescriptor } = useRouter();

  const formattedBreeds = useMemo((): CardItemBreedQuizResultData[] | undefined => {
    if (!breeds || !breeds.length) return;
    return breeds.map(
      (breed) =>
        ({
          name: breed.name,
          translatedName: breed.translations?.[0]?.name ?? breed.name,
          image: breed.imgs?.[0]?.small,
          matchPercentage: getMatchPercentage(answers, breed),
          attributes: cleanDeep({
            exercise: breed.exerciseIndex,
            education: breed.educationIndex,
            care: breed.careIndex,
            sociability: breed.sociabilityIndex,
          } as CardItemBreedQuizResultData['attributes']),
          infoLink: routeLocationDescriptor('breed', {
            params: [breed.translations?.[0]?.url ?? ''],
          }),
          littersLink: routeLocationDescriptor('litterList', {
            search: {
              breeds: [breed.name, breed.baseBreedName],
            },
          }),
          breedersLink: routeLocationDescriptor('breederList', {
            search: {
              breeds: [breed.name, breed.baseBreedName],
            },
          }),
          isSubscribed: !!breedogBreedInterestSubscription?.dogBreeds.find(
            (dogBreed) => dogBreed.name === breed.name,
          ),
        } as CardItemBreedQuizResultData),
    );
  }, [answers, breedogBreedInterestSubscription?.dogBreeds, breeds, routeLocationDescriptor]);

  return { formattedBreeds };
};
